import React from 'react';
import {CreateDeliveryForm, DistributionMarketer, DistributionProduct} from '../models/distribution.model';
import {Users, Package, ChevronDown} from 'lucide-react';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

interface DistributionFormFieldsProps {
  formData: CreateDeliveryForm;
  marketers: DistributionMarketer[];
  products: DistributionProduct[];
  selectedMarketer: DistributionMarketer | undefined;
  selectedProduct: DistributionProduct | undefined;
  onInputChange: (field: keyof CreateDeliveryForm, value: string) => void;
}

const DistributionFormFields: React.FC<DistributionFormFieldsProps> = ({
  formData,
  marketers,
  products,
  selectedMarketer,
  selectedProduct,
  onInputChange
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Marketer Selection */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Users className="h-4 w-4" />
          اختر المسوق
        </label>
        <div className="relative">
          <select
            value={formData.marketerId}
            onChange={(e) => onInputChange('marketerId', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500 appearance-none bg-white"
            required
          >
            <option value="">-- اختر المسوق --</option>
            {marketers.length > 0 ? (
              marketers.map((marketer) => (
                <option key={marketer.id} value={marketer.id}>
                  {marketer.fullName}
                </option>
              ))
            ) : (
              <option value="" disabled>جاري تحميل المسوقين...</option>
            )}
          </select>
          <ChevronDown className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
        {selectedMarketer && (
          <div className="mt-2 p-3 bg-brand-50 border border-brand-200 rounded-lg">
            <div className="text-sm">
              <p className="font-medium text-brand-800">{selectedMarketer.fullName}</p>
              <p className="text-brand-600">@{selectedMarketer.username}</p>
              {selectedMarketer.phone && (
                <p className="text-brand-600">📞 {selectedMarketer.phone}</p>
              )}
              {selectedMarketer.area && (
                <p className="text-brand-600">📍 {selectedMarketer.area}</p>
              )}
              <p className="text-brand-600">📦 {selectedMarketer.totalDeliveries} توزيع سابق</p>
            </div>
          </div>
        )}
      </div>

      {/* Product Selection */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" />
          اختر المنتج
        </label>
        <div className="relative">
          <select
            value={formData.productId}
            onChange={(e) => onInputChange('productId', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500 appearance-none bg-white"
            required
          >
            <option value="">-- اختر المنتج --</option>
            {products.length > 0 ? (
              products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} - {product.brand || 'غير محدد'} - {product.availableQuantity} متوفر
                </option>
              ))
            ) : (
              <option value="" disabled>جاري تحميل المنتجات...</option>
            )}
          </select>
          <ChevronDown className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
        {selectedProduct && (
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              {/* Product Image */}
              {selectedProduct.images && selectedProduct.images.length > 0 ? (
                <div className="flex-shrink-0">
                  <img
                    src={selectedProduct.images[0]}
                    alt={selectedProduct.name}
                    className="w-16 h-16 object-cover rounded-lg border border-green-200"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded-lg border border-green-200 flex items-center justify-center">
                  <Package className="w-6 h-6 text-gray-400" />
                </div>
              )}

              {/* Product Details */}
              <div className="flex-1 text-sm">
                <p className="font-medium text-green-800">{selectedProduct.name}</p>
                <p className="text-green-600">السعر: {formatCurrency(selectedProduct.price)}</p>
                <p className="text-green-600">متوفر: {selectedProduct.availableQuantity} قطعة</p>
                {selectedProduct.brand && (
                  <p className="text-green-600">🏷️ {selectedProduct.brand}</p>
                )}
                {selectedProduct.type && (
                  <p className="text-green-600">📦 {selectedProduct.type}</p>
                )}
                {selectedProduct.warehouseLocation && (
                  <p className="text-green-600">🏪 {selectedProduct.warehouseLocation}</p>
                )}
                {selectedProduct.hasSerial && (
                  <p className="text-green-600">🔢 يحتوي على أرقام تسلسلية</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DistributionFormFields;
